import React, { ReactNode, useEffect, useState } from "react";
import { Box, Collapse, Divider } from "@mui/material";
import type { SvgIconComponent } from "@mui/icons-material";
import { workbookCache } from "../../../excel/workbook/cache";
import { AccordionExpandIcon } from "./Accordion.styled";
import { ediphiPalette } from "../../styles/colors";
import { UiStateCacheKey } from "../../../excel/workbook/cacheKeys";

/** Survives a remount so a collapsed section doesn't flash open while the cache read is in flight. */
const expandedMemo = new Map<string, boolean>();

type DividerAccordionLayoutProps = {
  title: string;
  /** Sits left of the title, inside the divider's gap. */
  icon?: SvgIconComponent;
  children?: ReactNode;
} & (
  | { collapsible?: true; cacheKey: UiStateCacheKey }
  // A section that can't collapse has no state to persist, so a cacheKey would be dead weight.
  | { collapsible: false; cacheKey?: never }
);

/**
 * A collapsible section whose header is a divider: the whole row toggles it, and a caret at the head
 * of the row says so — right when closed, turned down when open, matching `AccordionLayout` and the
 * web app.
 *
 * Unlike `AccordionLayout` it starts expanded — collapsing is something the user opts into per
 * section, not a disclosure they have to open. Pass `collapsible={false}` to use it purely as a
 * labelled separator, where the sections are the page's structure rather than the user's to fold away.
 */
const DividerAccordionLayout = ({
  cacheKey,
  title,
  icon: Icon,
  children,
  collapsible = true,
}: DividerAccordionLayoutProps) => {
  const [expanded, setExpanded] = useState<boolean>(() => (cacheKey ? (expandedMemo.get(cacheKey) ?? true) : true));

  useEffect(() => {
    if (!cacheKey) return;
    let isCurrent = true;

    const restore = async () => {
      const cached = await workbookCache.get(cacheKey);
      if (!isCurrent || typeof cached !== "boolean") return;
      expandedMemo.set(cacheKey, cached);
      setExpanded(cached);
    };

    restore();

    return () => {
      isCurrent = false;
    };
  }, [cacheKey]);

  const handleToggle = () => {
    if (!cacheKey) return;
    const next = !expanded;
    expandedMemo.set(cacheKey, next);
    setExpanded(next);
    void workbookCache.set({ [cacheKey]: next });
  };

  // The divider-to-first-child gap lives with the content, so it animates away with a collapse
  // instead of leaving a hole under a collapsed divider.
  const body = <Box sx={{ display: "flex", flexDirection: "column", gap: 1, pt: 1 }}>{children}</Box>;

  return (
    <Box sx={{ display: "flex", flexDirection: "column" }}>
      <Box
        {...(collapsible && { role: "button", "aria-expanded": expanded, onClick: handleToggle })}
        sx={
          collapsible
            ? {
                cursor: "pointer",
                "&:hover .MuiDivider-root": {
                  color: ediphiPalette.brand600,
                  "&::before, &::after": { borderTopColor: ediphiPalette.brand600 },
                },
              }
            : undefined
        }
      >
        <Divider
          textAlign="left"
          // The stub and MUI's own wrapper inset both give way so the caret can start the row flush.
          sx={{
            fontSize: "12px",
            color: ediphiPalette.textMuted,
            ...(collapsible
              ? { "&::before": { width: 0 }, "& .MuiDivider-wrapper": { paddingLeft: 0 } }
              : { "&::before": { width: "8px" } }),
          }}
        >
          {/* Everything here inherits the divider's colour so it follows the hover highlight with
              the title. */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            {collapsible && (
              <AccordionExpandIcon
                sx={{
                  color: "inherit",
                  fontSize: "16px",
                  transform: expanded ? "rotate(90deg)" : "none",
                  transition: "transform 150ms",
                }}
              />
            )}
            {Icon && <Icon sx={{ fontSize: "16px", color: "inherit" }} />}
            {title}
          </Box>
        </Divider>
      </Box>
      {collapsible ? <Collapse in={expanded}>{body}</Collapse> : body}
    </Box>
  );
};

export default DividerAccordionLayout;
